import { FunctionComponent } from "react";
import { Button, Card, Col, Form, Modal, Pagination, Popover, Radio, Row, Select, Tooltip, Typography } from "antd";
import { CheckCircleFilled, CopyOutlined, DeleteOutlined, EditOutlined, EuroCircleTwoTone, PlusCircleOutlined, SaveOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";
import { TarifViewProps } from "./types";
import { InfosTarifEnfant } from "../../../components/admin/InfosTarifEnfant";
import { InfosTarifAdulte } from "../../../components/admin/InfosTarifAdulte";
import { ModalPeriode } from "../../../components/modals/ModalPeriode";

const { Text } = Typography;

export const TarifDesktopView: FunctionComponent<TarifViewProps> = ({
    application,
    periodesDto,
    periodesOptions,
    selectedIdPeriode,
    viewTarif,
    currentPage,
    openPopOver,
    modalPeriodeOpen,
    createPeriode,
    periodeToEdit,
    onApplicationChange,
    onSelectPeriode,
    onCreatePeriode,
    onModifierPeriode,
    onDeletePeriode,
    onCopierTarif,
    setCurrentPage,
    setOpenPopOver,
    setModalPeriodeOpen,
    isSelectedPeriodeReadOnly,
}) => {
    const periodesPerPage = 6;

    const handleDeletePeriode = (periode: any) => {
        Modal.confirm({
            title: 'Supprimer la période',
            content: `Êtes-vous sûr de vouloir supprimer la période ${periode.anneeDebut}/${periode.anneeFin} ?`,
            okText: 'Supprimer',
            okType: 'danger',
            cancelText: 'Annuler',
            onOk: () => onDeletePeriode(periode)
        });
    };

    const getPopOverCopierTarifContent = () => {
        return (
            <Row>
                <Col span={24}>
                    <Select
                        className="popover-content"
                        options={periodesOptions}
                        onChange={(value) => {
                            onCopierTarif(value);
                            setOpenPopOver(false);
                        }}
                    />
                </Col>
            </Row>
        );
    };

    const getPeriodeContent = () => {
        const startIndex = (currentPage - 1) * periodesPerPage;
        const endIndex = startIndex + periodesPerPage;
        const currentPeriodes = periodesDto?.slice(startIndex, endIndex) || [];

        return (
            <>
                <Row gutter={[16, 16]}>
                    {currentPeriodes.map((periode) => {
                        const isSelected = selectedIdPeriode === periode.id;
                        const hasTarifs = !!periode.existInscription;

                        return (
                            <Col xs={24} sm={12} md={8} lg={8} key={periode.id}>
                                <div style={{ position: 'relative' }}>
                                    {isSelected && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '-8px',
                                            zIndex: 2,
                                            background: '#fff',
                                            borderRadius: '50%',
                                            lineHeight: 1,
                                        }}>
                                            <CheckCircleFilled style={{ fontSize: '22px', color: '#1677ff' }} />
                                        </div>
                                    )}
                                    <Card
                                        hoverable
                                        bordered
                                        title={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <EuroCircleTwoTone />
                                                <span>{periode.anneeDebut}/{periode.anneeFin}</span>
                                            </span>
                                        }
                                        extra={
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                <Tooltip title="Modifier la période" color="geekblue">
                                                    <Button
                                                        icon={<EditOutlined />}
                                                        size="small"
                                                        type="default"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onModifierPeriode(periode);
                                                        }}
                                                    />
                                                </Tooltip>
                                                {!periode.existInscription && (
                                                    <Tooltip title="Supprimer la période" color="red">
                                                        <Button
                                                            icon={<DeleteOutlined />}
                                                            size="small"
                                                            danger
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeletePeriode(periode);
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </div>
                                        }
                                        className={isSelected ? 'periode-card-selected' : 'periode-card'}
                                        onClick={() => onSelectPeriode(periode)}
                                        style={{
                                            border: isSelected ? '2px solid #1677ff' : '1px solid #e8e8e8',
                                            cursor: 'pointer',
                                            transition: 'all 0.25s ease',
                                            borderRadius: '8px',
                                            background: isSelected ? '#f0f5ff' : '#fff',
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '10px' }}>
                                            {periode.dateDebut} — {periode.dateFin}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: hasTarifs ? '#52c41a' : '#d9d9d9',
                                                flexShrink: 0,
                                                display: 'inline-block',
                                            }} />
                                            <Text style={{ fontSize: '12px', color: hasTarifs ? '#52c41a' : '#8c8c8c' }}>
                                                {hasTarifs ? 'Tarifs configurés' : 'À configurer'}
                                            </Text>
                                        </div>
                                    </Card>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
                {periodesDto && periodesDto.length > periodesPerPage && (
                    <Row style={{ marginTop: '24px', justifyContent: 'center' }}>
                        <Col>
                            <Pagination
                                current={currentPage}
                                total={periodesDto.length}
                                pageSize={periodesPerPage}
                                onChange={(page) => setCurrentPage(page)}
                                showSizeChanger={false}
                                showTotal={(total, range) => `${range[0]}-${range[1]} sur ${total} périodes`}
                            />
                        </Col>
                    </Row>
                )}
            </>
        );
    };

    return (
        <>
            {/* Barre de sélection : type de tarif + bouton nouvelle période */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '20px',
                gap: '16px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: '13px', color: '#595959', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        Type de tarif
                    </Text>
                    <Form.Item name="typeTarif" style={{ margin: 0 }} initialValue="COURS_ENFANT">
                        <Radio.Group
                            onChange={(e) => onApplicationChange(e.target.value)}
                            optionType="button"
                            buttonStyle="solid"
                        >
                            <Radio.Button value="COURS_ENFANT">
                                <UserOutlined style={{ marginRight: 6 }} />
                                Cours enfant
                            </Radio.Button>
                            <Radio.Button value="COURS_ADULTE">
                                <TeamOutlined style={{ marginRight: 6 }} />
                                Cours adulte
                            </Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                </div>
                <Button
                    icon={<PlusCircleOutlined />}
                    type="primary"
                    onClick={onCreatePeriode}
                >
                    Nouvelle période
                </Button>
            </div>

            {/* Titre de section périodes */}
            <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '15px', color: '#262626' }}>Périodes</Text>
            </div>

            {periodesOptions && getPeriodeContent()}

            {viewTarif && application === "COURS_ENFANT" && (
                <InfosTarifEnfant readOnly={isSelectedPeriodeReadOnly()} />
            )}
            {viewTarif && application === "COURS_ADULTE" && (
                <InfosTarifAdulte readOnly={isSelectedPeriodeReadOnly()} />
            )}

            {/* Barre d'actions bas de page */}
            {viewTarif && !isSelectedPeriodeReadOnly() && (
                <Card
                    size="small"
                    style={{
                        marginTop: '24px',
                        background: '#fafafa',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px',
                    }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                        <Text style={{ fontSize: '13px', color: '#595959' }}>
                            Tarifs de la période sélectionnée
                        </Text>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Popover
                                content={getPopOverCopierTarifContent()}
                                title="Copier les tarifs depuis une période"
                                trigger="click"
                                open={openPopOver}
                                onOpenChange={setOpenPopOver}
                            >
                                <Button icon={<CopyOutlined />} type="default">
                                    Copier
                                </Button>
                            </Popover>
                            <Button icon={<SaveOutlined />} type="primary" htmlType="submit">
                                Enregistrer
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            <ModalPeriode
                open={modalPeriodeOpen}
                setOpen={setModalPeriodeOpen}
                isCreation={createPeriode}
                periode={periodeToEdit}
                application={application}
            />
        </>
    );
};
